import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const inputBase =
  'w-full rounded-[16px] border bg-surface px-4 text-[16px] text-fg placeholder:text-muted transition-colors focus:outline-none focus-visible:border-fg';

interface FieldWrapProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  id: string;
  children: ReactNode;
  className?: string;
  counter?: ReactNode;
}

function FieldWrap({ label, hint, error, id, children, className, counter }: FieldWrapProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {counter && <span className="text-xs tabular-nums text-muted">{counter}</span>}
      </div>
      {children}
      {error ? (
        <p id={`${id}-err`} className="text-sm font-medium text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  counter?: ReactNode;
  wrapClassName?: string;
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(function InputField({ label, hint, error, counter, wrapClassName, className, id, ...rest }, ref) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldWrap label={label} hint={hint} error={error} id={fid} className={wrapClassName} counter={counter}>
      <input
        ref={ref}
        id={fid}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? `${fid}-err` : hint ? `${fid}-hint` : undefined}
        className={cn(inputBase, 'h-12', error ? 'border-danger' : 'border-line', className)}
        {...rest}
      />
    </FieldWrap>
  );
});

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  counter?: ReactNode;
  wrapClassName?: string;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(function TextAreaField({ label, hint, error, counter, wrapClassName, className, id, ...rest }, ref) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldWrap label={label} hint={hint} error={error} id={fid} className={wrapClassName} counter={counter}>
      <textarea
        ref={ref}
        id={fid}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? `${fid}-err` : hint ? `${fid}-hint` : undefined}
        className={cn(inputBase, 'min-h-[96px] resize-y py-3', error ? 'border-danger' : 'border-line', className)}
        {...rest}
      />
    </FieldWrap>
  );
});

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  wrapClassName?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField({ label, hint, error, wrapClassName, className, id, children, ...rest }, ref) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldWrap label={label} hint={hint} error={error} id={fid} className={wrapClassName}>
      <select ref={ref} id={fid} aria-invalid={!!error || undefined} className={cn(inputBase, 'h-12 appearance-none', error ? 'border-danger' : 'border-line', className)} {...rest}>
        {children}
      </select>
    </FieldWrap>
  );
});

export function Switch({ checked, onChange, label, description, disabled, busy, testId }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string; disabled?: boolean; busy?: boolean; testId?: string }) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <label htmlFor={id} className="text-[15px] font-semibold">
          {label}
        </label>
        {description && <p className="text-sm text-muted">{description}</p>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
        data-testid={testId}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-7 w-12 shrink-0 rounded-full border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg disabled:opacity-40',
          checked ? 'border-green bg-green' : 'border-line bg-raised',
        )}
      >
        <span className={cn('absolute left-0 top-0.5 h-[22px] w-[22px] rounded-full transition-transform', checked ? 'translate-x-[22px] bg-ground' : 'translate-x-0.5 bg-muted')} />
      </button>
    </div>
  );
}
